<template>
  <b-field :class="$style.continuousDiscreteInputContainer">
    <b-checkbox
      :class="$style.continuousDiscreteRangeCheckbox"
      v-model="isContinuousDiscreteRangeEnabled"
    >
      Set a minimum and maximum value (Optional)
    </b-checkbox>
    <div class="columns" v-if="isContinuousDiscreteRangeEnabled">
      <div class="column">
        <ValidationProvider
          v-slot="{ errors }"
          :rules="`required`"
          name="Min Value"
        >
          <b-field
            label="Min"
            :type="errors.length ? 'is-danger' : undefined"
            :message="errors[0]"
          >
            <b-input type="number" v-model="rangeConfigMin" step="any" />
          </b-field>
          <p v-if="minRangeError" :class="$style.validationErrorMessage">
            Min Value cannot be greater than Max Value
          </p>
        </ValidationProvider>
      </div>
      <div class="column">
        <ValidationProvider
          v-slot="{ errors }"
          :rules="`required`"
          name="Max Value"
        >
          <b-field
            label="Max"
            :type="errors.length ? 'is-danger' : undefined"
            :message="errors[0]"
          >
            <b-input type="number" v-model="rangeConfigMax" step="any" />
          </b-field>
          <p v-if="maxRangeError" :class="$style.validationErrorMessage">
            Max Value cannot be lesser than Min Value
          </p>
        </ValidationProvider>
      </div>
    </div>
  </b-field>
</template>

<script lang="ts">
import { Component, Prop, Watch, Vue } from 'vue-property-decorator';
import {
  ContinuousDiscreteCriterionContent,
  Criterion,
} from '../../../store/types/criterions.types';
import { ValidationProvider, ValidationObserver } from 'vee-validate';

@Component({
  components: { ValidationProvider, ValidationObserver },
})
export default class ContinuousDiscreteCriterionInput extends Vue {
  @Prop()
  public dirtyCriterion!: Criterion;
  public isContinuousDiscreteRangeEnabled: boolean = false;
  public minRangeError: boolean = false;
  public maxRangeError: boolean = false;
  public rangeConfigMin: number = 0;
  public rangeConfigMax: number = 0;

  @Watch('isContinuousDiscreteRangeEnabled')
  public watchIsContinuousDiscreteRangeEnabled(newValue: boolean) {
    if (
      this.dirtyCriterion.content instanceof ContinuousDiscreteCriterionContent
    ) {
      if (newValue) {
        this.dirtyCriterion.content.config = this.dirtyCriterion.content.config
          ? this.dirtyCriterion.content.config
          : [1, 10];
      } else {
        this.dirtyCriterion.content.config = null;
        this.minRangeError = false;
        this.maxRangeError = false;
        this.$emit('disableSubmitButton', false);
      }
    }
  }

  @Watch('dirtyCriterion.content.config', {
    deep: true,
    immediate: true,
  })
  public onRangeConfigChanged(rangeConfig: any) {
    if (rangeConfig) {
      const rangeConfigArray: any[] =
        typeof rangeConfig === 'string' ? rangeConfig.split(', ') : rangeConfig;

      this.isContinuousDiscreteRangeEnabled = true;
      this.rangeConfigMin = rangeConfigArray[0];
      this.rangeConfigMax = rangeConfigArray[1];
    }
  }

  @Watch('rangeConfigMin')
  public onMinChanged(minRange: number) {
    this.minRangeError =
      minRange.toString() !== '' &&
      Number(minRange) >= Number(this.rangeConfigMax)
        ? true
        : false;
    if (this.maxRangeError) {
      this.maxRangeError = this.minRangeError;
    }

    if (
      !this.minRangeError &&
      !this.maxRangeError &&
      this.dirtyCriterion.content instanceof ContinuousDiscreteCriterionContent
    ) {
      const updatedRangeConfig = [minRange, this.rangeConfigMax];
      this.dirtyCriterion.content.config = updatedRangeConfig;
      this.$emit('update:dirtyCriterion', this.dirtyCriterion);
    }

    this.$emit('disableSubmitButton', this.minRangeError);
  }

  @Watch('rangeConfigMax')
  public onMaxChanged(maxRange: number) {
    this.maxRangeError =
      maxRange.toString() !== '' &&
      Number(maxRange) <= Number(this.rangeConfigMin)
        ? true
        : false;
    if (this.minRangeError) {
      this.minRangeError = this.maxRangeError;
    }

    if (
      !this.minRangeError &&
      !this.maxRangeError &&
      this.dirtyCriterion.content instanceof ContinuousDiscreteCriterionContent
    ) {
      const updatedRangeConfig = [this.rangeConfigMin, maxRange];
      this.dirtyCriterion.content.config = updatedRangeConfig;
      this.$emit('update:dirtyCriterion', this.dirtyCriterion);
    }

    this.$emit('disableSubmitButton', this.maxRangeError);
  }
}
</script>

<style lang="scss" module>
.continuousDiscreteInputContainer {
  display: flex;
  flex-direction: column;
}
.continuousDiscreteRangeCheckbox {
  margin-top: 12px;
  font-size: 14px;
  color: #71797c;
  margin-bottom: 10px;
}
.validationErrorMessage {
  color: red;
  font-size: 12px;
  padding-bottom: 10px;
}
</style>
